import React, { useState, useEffect } from "react";
import { getGlobalParamFilterList } from "../../../redux/actions/globalParamFilterSlice";
import { useDispatch, useSelector } from "react-redux";
import { saveParamConfig } from "../../../redux/actions/parameterConfigSlice";
import { updateParamConfig } from "../../../redux/actions/updateParameterConfigSlice";
import { getActiveParameterConfigurations } from "../../../redux/actions/activeParameterConfigSlice";
import { getActiveGlobalRiskConfig } from "../../../redux/actions/activeGlobalRiskConfigSlice";
import { Box } from "@mui/material";
import { toast } from 'react-toastify';
import { MultiDropdown } from "../../../components/common/MultiDropdown";
import { updateParameterConfiguration } from "../../../common/endpoints";
import Paramsliders, { DEFAULTS } from "./Paramsliders";
import Modal from "../../../components/common/modal/Modal";
import ButtonComponent from "../../../components/common/button/Button";

const toNumber = (val) => {
  if (val === null || val === undefined || val === "") return NaN;
  return Number(val);
};

const mapGlobalConfigToThresholds = (config) => {
  if (!config) return structuredClone(DEFAULTS);

  const pickRange = (fromVal, toVal, defRange) => {
    const fromNum = toNumber(fromVal);
    const toNum = toNumber(toVal);
    const safeFrom = Number.isFinite(fromNum) ? fromNum : defRange[0];
    const safeTo = Number.isFinite(toNum) ? toNum : defRange[1];
    return [safeFrom, safeTo];
  };

  return {
    high: {
      pdt: pickRange(config?.high?.pdtFrom, config?.high?.pdtTo, DEFAULTS.high.pdt),
      variance: pickRange(config?.high?.varFrom, config?.high?.varTo, DEFAULTS.high.variance),
      safetyWeeks: Number.isFinite(toNumber(config?.high?.safetyWeeks))
        ? Number(config.high.safetyWeeks)
        : DEFAULTS.high.safetyWeeks,
    },
    medium: {
      pdt: pickRange(config?.medium?.pdtFrom, config?.medium?.pdtTo, DEFAULTS.medium.pdt),
      variance: pickRange(config?.medium?.varFrom, config?.medium?.varTo, DEFAULTS.medium.variance),
      safetyWeeks: Number.isFinite(toNumber(config?.medium?.safetyWeeks))
        ? Number(config.medium.safetyWeeks)
        : (DEFAULTS.medium.safetyWeeks ?? 0),
    },
    low: {
      pdt: pickRange(config?.low?.pdtFrom, config?.low?.pdtTo, DEFAULTS.low.pdt),
      variance: pickRange(config?.low?.varFrom, config?.low?.varTo, DEFAULTS.low.variance),
      safetyWeeks: Number.isFinite(toNumber(config?.low?.safetyWeeks))
        ? Number(config.low.safetyWeeks)
        : (DEFAULTS.low.safetyWeeks ?? 0),
    },
  };
};

const ParamSlidersModal = ({ open, onClose, onSave, onUpdate, mode = "create", initialRule, rules = [], onHighlightExisting }) => {
  // Multi-dropdown filter state (dashboard pattern)
  const [filters, setFilters] = useState({ plant: [], material: [], sCode: [], mrpController: [] });
  const [appliedFilters, setAppliedFilters] = useState({ plant: [], material: [], sCode: [], mrpController: [] });
  const [thresholds, setThresholds] = useState(DEFAULTS);
  const [recentlyChanged, setRecentlyChanged] = useState(false);
  const [hasUserEdited, setHasUserEdited] = useState(false);
  const [openDropdownName, setOpenDropdownName] = useState(null);
  const dispatch = useDispatch();
  const loading = useSelector((state) => state.parameterConfig?.loading ?? false);
  const updateLoading = useSelector((state) => state.updateParameterConfig?.loading ?? false);
  const EMPTY_ARR = React.useMemo(() => [], []);
  const EMPTY_OBJ = React.useMemo(() => ({}), []);
  const filtersData = useSelector((state) => state.globalParamFilter?.data?.data?.filters ?? EMPTY_OBJ);
  const filtersLoading = useSelector((state) => state.globalParamFilter?.loading ?? false);
  const activeConfigs = useSelector((state) => state.activeParameterConfig?.data?.data ?? EMPTY_ARR);
  const globalConfig = useSelector((state) => state.activeGlobalRiskConfig?.data?.data ?? null);
  const globalConfigLoading = useSelector((state) => state.activeGlobalRiskConfig?.loading ?? false);

  // Fetch filters on open (create mode) and on selection change
  // Fetch filters on open (create mode)
  // On modal open, fetch all options for empty filters
  useEffect(() => {
    if (open && mode === "create") {
      if (!globalConfig && !globalConfigLoading) {
        dispatch(getActiveGlobalRiskConfig());
      }
      dispatch(getGlobalParamFilterList({ plant: [], material: [], sCode: [], mrpController: [], pageNo: 0, pageSize: 1000 }));
      setFilters({ plant: [], material: [], sCode: [], mrpController: [] });
      setAppliedFilters({ plant: [], material: [], sCode: [], mrpController: [] });
      setThresholds(structuredClone(DEFAULTS));
      setHasUserEdited(false);
    }
  }, [open, mode, dispatch]);

  useEffect(() => {
    if (!open) {
      setOpenDropdownName(null);
      setHasUserEdited(false);
    }
  }, [open]);

  useEffect(() => {
    if (!open || mode !== "create" || !globalConfig || hasUserEdited) return;
    setThresholds(mapGlobalConfigToThresholds(globalConfig));
  }, [open, mode, globalConfig, hasUserEdited]);

  // Refetch filters when any selection changes (create mode only)
  // Refetch filters when any selection changes (create mode only)
  // Option helpers (merge API options with applied values)
  // filtersData and filtersLoading are selected above to avoid returning whole slice objects
  const mergeUnique = (apiArr = [], selectedArr = []) => {
    const selVals = (selectedArr || []).map(s => (s && typeof s === 'object' ? s.value : s)).filter(Boolean);
    const combined = Array.from(new Set([...(apiArr || []), ...selVals]));
    return combined.map(v => ({ title: String(v ?? ""), value: v }));
  };
  const plantOptions = mergeUnique(filtersData.plant, appliedFilters.plant);
  const materialOptions = mergeUnique(filtersData.material, appliedFilters.material);
  const sCodeOptions = mergeUnique(filtersData.sCode, appliedFilters.sCode);
  const mrpOptions = mergeUnique(filtersData.mrpController, appliedFilters.mrpController);
  // (No-op: all options are derived from mergeUnique, no setState needed)

  // For edit mode, preselect values if present
  useEffect(() => {
    if (open && mode === "edit" && initialRule) {
      const toSelectedItems = (val) => {
        if (val == null) return [];
        if (Array.isArray(val)) return val.map(v => ({ title: String(v), value: v }));
        return [{ title: String(val), value: val }];
      };
      const scodeVal = initialRule.scode ?? initialRule.sCode;
      setFilters({
        plant: initialRule.plant ? [{ title: String(initialRule.plant), value: initialRule.plant }] : [],
        material: initialRule.material ? [{ title: String(initialRule.material), value: initialRule.material }] : [],
        sCode: toSelectedItems(scodeVal),
        mrpController: initialRule.mrpController ? [{ title: String(initialRule.mrpController), value: initialRule.mrpController }] : [],
      });
      setAppliedFilters({
        plant: initialRule.plant ? [{ title: String(initialRule.plant), value: initialRule.plant }] : [],
        material: initialRule.material ? [{ title: String(initialRule.material), value: initialRule.material }] : [],
        sCode: toSelectedItems(scodeVal),
        mrpController: initialRule.mrpController ? [{ title: String(initialRule.mrpController), value: initialRule.mrpController }] : [],
      });
      // Normalize thresholds: backend may return nested `thresholds` or flat fields (highPdtFrom, highPdtTo, ...)
      const hasVal = (v) => v !== undefined && v !== null;
      const norm = (rule) => {
        if (rule.thresholds && rule.thresholds.high) return rule.thresholds;

        const pickRange = (fromKey, toKey, defRange) => {
          const f = rule[fromKey];
          const t = rule[toKey];
          if (hasVal(f) || hasVal(t)) return [hasVal(f) ? f : defRange[0], hasVal(t) ? t : defRange[1]];
          return defRange.slice();
        };

        return {
          high: {
            pdt: pickRange('highPdtFrom', 'highPdtTo', DEFAULTS.high.pdt),
            variance: pickRange('highVarFrom', 'highVarTo', DEFAULTS.high.variance),
            safetyWeeks: hasVal(rule.highSafetyWeeks) ? rule.highSafetyWeeks : DEFAULTS.high.safetyWeeks,
          },
          medium: {
            pdt: pickRange('mediumPdtFrom', 'mediumPdtTo', DEFAULTS.medium.pdt),
            variance: pickRange('mediumVarFrom', 'mediumVarTo', DEFAULTS.medium.variance),
            safetyWeeks: hasVal(rule.mediumSafetyWeeks) ? rule.mediumSafetyWeeks : (DEFAULTS.medium.safetyWeeks ?? 0),
          },
          low: {
            pdt: pickRange('lowPdtFrom', 'lowPdtTo', DEFAULTS.low.pdt),
            variance: pickRange('lowVarFrom', 'lowVarTo', Array.isArray(DEFAULTS.low.variance) ? DEFAULTS.low.variance : [DEFAULTS.low.variance, DEFAULTS.low.variance]),
            safetyWeeks: hasVal(rule.lowSafetyWeeks) ? rule.lowSafetyWeeks : (DEFAULTS.low.safetyWeeks ?? 0),
          },
        };
      };

      setThresholds(norm(initialRule) || DEFAULTS);
    }
  }, [open, mode, initialRule]);
  // MultiDropdown handlers
  const handleChangeItems = (name, items) => {
    setFilters(prev => ({ ...prev, [name]: items || [] }));
  };

  const handleApplyItems = (name, items) => {
    // Update appliedFilters and fetch options for all fields
    const nextApplied = { ...appliedFilters, [name]: items || [] };
    setAppliedFilters(nextApplied);
    dispatch(getGlobalParamFilterList({
      plant: nextApplied.plant.map(i => i.value),
      material: nextApplied.material.map(i => i.value),
      sCode: nextApplied.sCode.map(i => i.value),
      mrpController: nextApplied.mrpController.map(i => i.value),
      pageNo: 0,
      pageSize: 1000,
    }));
  };

  const handleOpenDropdown = (name) => {
    setOpenDropdownName(name);
    // Defer fetch to avoid updating state during Autocomplete render
    const payload = {
      plant: name === "plant" ? [] : appliedFilters.plant.map(i => i.value),
      material: name === "material" ? [] : appliedFilters.material.map(i => i.value),
      sCode: name === "sCode" ? [] : appliedFilters.sCode.map(i => i.value),
      mrpController: name === "mrpController" ? [] : appliedFilters.mrpController.map(i => i.value),
      pageNo: 0,
      pageSize: 1000,
    };
    dispatch(getGlobalParamFilterList(payload))
   
  };

  const isEdit = mode === "edit";
  const isFiltersDisabled = isEdit || (filtersLoading && !openDropdownName);
  // Debounce: disable save for 500ms after slider change
  const handleThresholdsChange = (val) => {
    setThresholds(val);
    setHasUserEdited(true);
    setRecentlyChanged(true);
    setTimeout(() => setRecentlyChanged(false), 500);
  };

  return (
    <>
      <Modal
        open={open}
        handleClose={onClose}
        maxWidth="md"
        fullWidth={true}
        title={isEdit ? "Edit Operational Override" : "Create Exception Rule"}
        content={
          <Box>
            <Box
              sx={{
                display: "grid",
                gridTemplateColumns: "repeat(2, minmax(0, 1fr))",
                gap: 2,
                mb: 2,
              }}
            >
              <Box sx={{ minWidth: 350 }}>
                <Filter label="Plant Code" required>
                <MultiDropdown
                  id="plant-dropdown"
                  options={plantOptions}
                  name="plant"
                  onChangeItems={handleChangeItems}
                  onApplyItems={handleApplyItems}
                  showApply={true}
                  onOpenDropdown={handleOpenDropdown}
                  selectedItemsData={filters.plant}
                  disablePortal={true}
                  dropdownWidth="100%"
                  disabled={isFiltersDisabled}
                />
                </Filter>
              </Box>
              <Box sx={{ minWidth: 350 }}>
                <Filter label="External Material Group" required>
                <MultiDropdown
                  id="scode-dropdown"
                  options={sCodeOptions}
                  name="sCode"
                  onChangeItems={handleChangeItems}
                  onApplyItems={handleApplyItems}
                  showApply={true}
                  onOpenDropdown={handleOpenDropdown}
                  selectedItemsData={filters.sCode}
                  disablePortal={true}
                  dropdownWidth="100%"
                  disabled={isFiltersDisabled}
                />
                </Filter>
              </Box>

              <Box sx={{ minWidth: 350 }}>
                <Filter label="MRP Controller" required>
                <MultiDropdown
                  id="mrp-dropdown"
                  options={mrpOptions}
                  name="mrpController"
                  onChangeItems={handleChangeItems}
                  onApplyItems={handleApplyItems}
                  showApply={true}
                  onOpenDropdown={handleOpenDropdown}
                  selectedItemsData={filters.mrpController}
                  disablePortal={true}
                  dropdownWidth="100%"
                  disabled={isFiltersDisabled}
                />
                </Filter>
              </Box>
              <Box sx={{ minWidth: 350 }}>
                <Filter label="TCCC Material Number" required>
                <MultiDropdown
                  id="material-dropdown"
                  options={materialOptions}
                  name="material"
                  onChangeItems={handleChangeItems}
                  onApplyItems={handleApplyItems}
                  showApply={true}
                  onOpenDropdown={handleOpenDropdown}
                  selectedItemsData={filters.material}
                  disablePortal={true}
                  dropdownWidth="100%"
                  disabled={isFiltersDisabled}
                />
                </Filter>

              </Box>
            </Box>
            <Paramsliders value={thresholds} onChange={handleThresholdsChange} showSave={false} />
          </Box>
        }
        action={
          <Box sx={{ display: "flex", gap: 2, justifyContent: "flex-end" }}>
            <ButtonComponent
              onClick={onClose}
              label="Discard"
              type="primary"
              bgColor="white"
              color="black"
            />
            {isEdit && (
              <ButtonComponent
                label="Reset to global default"
                type="primary"
                bgColor="#e0e0e0"
                color="black"
                onClick={() => setThresholds(structuredClone(DEFAULTS))}
              />
            )}
            {isEdit ? (
              <ButtonComponent
                label={updateLoading ? "Updating..." : "Update Override"}
                type="primary"
                bgColor="red"
                color="white"
                disabled={updateLoading || filters.plant.length === 0 || filters.material.length === 0}
                onClick={async () => {
                  // Prepare API payload as per backend contract
                  const payload = {
                    plant: filters.plant.map(i => i.value),
                    material: filters.material.map(i => i.value),
                    scode: filters.sCode.map(i => i.value),
                    mrpController: filters.mrpController.map(i => i.value),
                    highPdtFrom: thresholds.high?.pdt?.[0] ?? 0,
                    highPdtTo: thresholds.high?.pdt?.[1] ?? 0,
                    highVarFrom: thresholds.high?.variance?.[0] ?? 0,
                    highVarTo: thresholds.high?.variance?.[1] ?? 0,
                    highSafetyWeeks: thresholds.high?.safetyWeeks ?? 0,
                    mediumPdtFrom: thresholds.medium?.pdt?.[0] ?? 0,
                    mediumPdtTo: thresholds.medium?.pdt?.[1] ?? 0,
                    mediumVarFrom: thresholds.medium?.variance?.[0] ?? 0,
                    mediumVarTo: thresholds.medium?.variance?.[1] ?? 0,
                    lowPdtFrom: thresholds.low?.pdt?.[0] ?? 0,
                    lowPdtTo: thresholds.low?.pdt?.[1] ?? 0,
                    lowVarFrom: Array.isArray(thresholds.low?.variance) ? thresholds.low?.variance?.[0] : thresholds.low?.variance ?? 0,
                    lowVarTo: Array.isArray(thresholds.low?.variance) ? thresholds.low?.variance?.[1] : thresholds.low?.variance ?? 0,
                  };
                  // Use backend id if available, fallback to initialRule.id
                  let backendId = initialRule?.id;
                  // Try to find the rule in the rules array by matching plant/material/sCode/mrpController if backendId looks like a local id
                  if (backendId && (typeof backendId === 'number' || String(backendId).length < 10)) {
                    const rulesArr = window?.rulesState || [];
                    const match = rulesArr.find(r =>
                      r.plant === (filters.plant[0]?.value) &&
                      r.materialId === (filters.material[0]?.value) &&
                      ((r.scode ?? r.sCode) === (filters.sCode[0]?.value)) &&
                      r.mrpController === (filters.mrpController[0]?.value) &&
                      typeof r.id === 'string' && r.id.length > 10
                    );
                    if (match) backendId = match.id;
                  }
                  const result = await dispatch(updateParamConfig({ data: payload, dynamicEndpoint: `${updateParameterConfiguration}/${backendId}` }));
                  if (result?.payload?.status === "SUCCESS") {
                    toast.success("Parameter configuration updated successfully", { autoClose: 4000 });
                    onUpdate?.(result.payload.data);
                    // Refresh active parameter configurations so table updates immediately
                    dispatch(getActiveParameterConfigurations());
                    onClose?.();
                  }
                }}
              />
            ) : (
              <ButtonComponent
                label={loading ? "Saving..." : recentlyChanged ? "Wait..." : "Add Exception"}
                type="primary"
                bgColor="red"
                color="white"
                disabled={
                  filters.plant.length === 0
                  || filters.material.length === 0
                  || filters.sCode.length === 0
                  || filters.mrpController.length === 0
                  || loading
                  || recentlyChanged
                }
                onClick={async () => {
                  // Prepare API payload as per backend contract
                  const payload = {
                    plant: filters.plant.map(i => i.value),
                    material: filters.material.map(i => i.value),
                    scode: filters.sCode.map(i => i.value),
                    mrpController: filters.mrpController.map(i => i.value),
                    highPdtFrom: thresholds.high?.pdt?.[0] ?? 0,
                    highPdtTo: thresholds.high?.pdt?.[1] ?? 0,
                    highVarFrom: thresholds.high?.variance?.[0] ?? 0,
                    highVarTo: thresholds.high?.variance?.[1] ?? 0,
                    highSafetyWeeks: thresholds.high?.safetyWeeks ?? 0,
                    mediumPdtFrom: thresholds.medium?.pdt?.[0] ?? 0,
                    mediumPdtTo: thresholds.medium?.pdt?.[1] ?? 0,
                    mediumVarFrom: thresholds.medium?.variance?.[0] ?? 0,
                    mediumVarTo: thresholds.medium?.variance?.[1] ?? 0,
                    lowPdtFrom: thresholds.low?.pdt?.[0] ?? 0,
                    lowPdtTo: thresholds.low?.pdt?.[1] ?? 0,
                    lowVarFrom: Array.isArray(thresholds.low?.variance) ? thresholds.low?.variance?.[0] : thresholds.low?.variance ?? 0,
                    lowVarTo: Array.isArray(thresholds.low?.variance) ? thresholds.low?.variance?.[1] : thresholds.low?.variance ?? 0,
                  };
                  // Duplicate check: normalize arrays/objects/strings and compare sorted keys
                  const normalizeValue = (v) => {
                    if (v == null) return [];
                    if (Array.isArray(v)) {
                      const mapped = v
                        .map((el) => {
                          if (el == null) return '';
                          if (typeof el === 'object') {
                            return (el.value ?? el.title ?? el.id ?? '').toString();
                          }
                          return el.toString();
                        })
                        .map(s => s.trim())
                        .filter(Boolean);
                      return Array.from(new Set(mapped)).sort();
                    }
                    if (typeof v === 'string' && v.includes(',')) {
                      return Array.from(new Set(v.split(',').map(s => s.trim()).filter(Boolean))).sort();
                    }
                    return [String(v).trim()].filter(Boolean);
                  };

                  const keyFor = (pArr, mArr, sArr, rArr) => {
                    const k = [pArr || [], mArr || [], sArr || [], rArr || []]
                      .map(arr => (Array.isArray(arr) ? arr.slice().map(String).map(s => s.trim()).filter(Boolean).sort().join('|') : String(arr || '').trim()))
                      .join('::');
                    return k;
                  };

                  const np = normalizeValue(payload.plant);
                  const nm = normalizeValue(payload.material);
                  const ns = normalizeValue(payload.scode);
                  const nr = normalizeValue(payload.mrpController);
                  const newKey = keyFor(np, nm, ns, nr);

                  let found = false;
                  let foundId = null;
                  // Ensure we have fresh active configs from server before checking duplicates
                  let freshLookup = activeConfigs;
                  try {
                    const res = await dispatch(getActiveParameterConfigurations());
                    freshLookup = res?.payload?.data?.data ?? activeConfigs;
                  } catch (e) {
                    // ignore fetch error and fallback to current activeConfigs or rules
                    freshLookup = activeConfigs;
                  }
                  const lookup = (freshLookup && freshLookup.length > 0) ? freshLookup : (rules || []);
                  for (const rr of lookup) {
                    const rp = normalizeValue(rr.plant ?? rr.plants ?? rr.plantIds ?? rr.plantId);
                    const rm = normalizeValue(rr.material ?? rr.materialId ?? rr.materials ?? rr.materialIds);
                    const rs = normalizeValue(rr.sCode ?? rr.scode ?? rr.sCodes ?? rr.scodes);
                    const rrp = normalizeValue(rr.mrpController ?? rr.mrpControllers ?? rr.mrpControllerId ?? rr.mrpControllerIds);
                    const existingKey = keyFor(rp, rm, rs, rrp);
                    if (existingKey === newKey) { found = true; foundId = rr.id; break; }
                  }
                  if (found) {
                    toast.error('Exceptional rule already exists in Operational Overrides. Update the existing rule instead of creating a duplicate.', { autoClose: 4000 });
                    // ask parent to highlight the existing row
                    onHighlightExisting?.(foundId ?? null);
                    onClose?.();
                    return; // do not save duplicate
                  }

                  // Secondary check: include `scode` in primary key to detect duplicates
                  const primaryKeyFor = (pArr, mArr, sArr, rArr) => [pArr || [], mArr || [], sArr || [], rArr || []]
                    .map(arr => (Array.isArray(arr) ? arr.slice().map(String).map(s => s.trim()).filter(Boolean).sort().join('|') : String(arr || '').trim()))
                    .join('::');

                  const newPrimary = primaryKeyFor(np, nm, ns, nr);
                  let primaryFound = false;
                  for (const rr of lookup) {
                    const rp = normalizeValue(rr.plant ?? rr.plants ?? rr.plantIds ?? rr.plantId);
                    const rm = normalizeValue(rr.material ?? rr.materialId ?? rr.materials ?? rr.materialIds);
                    const rs = normalizeValue(rr.sCode ?? rr.scode ?? rr.sCodes ?? rr.scodes);
                    const rrp = normalizeValue(rr.mrpController ?? rr.mrpControllers ?? rr.mrpControllerId ?? rr.mrpControllerIds);
                    const existingPrimary = primaryKeyFor(rp, rm, rs, rrp);
                    if (existingPrimary === newPrimary) { primaryFound = true; foundId = rr.id; break; }
                  }
                  if (primaryFound) {
                    toast.error('An operational override already exists for the same Plant / Material / MRP Controller. Update the existing rule instead of creating a duplicate.', { autoClose: 5000 });
                    onHighlightExisting?.(foundId ?? null);
                    onClose?.();
                    return; // block save when primary key already exists
                  }

                  const result = await dispatch(saveParamConfig({ data: payload }));
                  if (result?.payload?.status === "SUCCESS") {
                    toast.success("Parameter configuration saved successfully", { autoClose: 4000 });
                    onSave?.(result.payload.data?.[0]);
                    // Refresh active parameter configurations so table updates immediately
                    dispatch(getActiveParameterConfigurations());
                    onClose?.();
                  }
                }}
              />
            )}
          </Box>
        }
      />
    </>
  );
};



/* =======================
   FILTER WRAPPER
   ======================= */
const Filter = ({ label, children, required = false }) => (
  <div style={{ display: "flex", flexDirection: "column", gap: 6, width: "100%", minWidth: 0 }}>
    <span style={{ fontSize: 13, fontWeight: 700 }}>
      {label}
      {required && <span style={{ color: "#d32f2f" }}> *</span>}
    </span>
    <div style={{ width: "100%" }}>{children}</div>
  </div>
);


export default ParamSlidersModal;